import React, { useState, useRef } from 'react';
import Breadcrumb from '../components/Breadcrumb';
import PlantCard from '../components/PlantCard';
import PlantListItem from '../components/plants/PlantListItem';
import FiltersBar from '../components/plants/FiltersBar';
import { usePlants } from '../hooks/usePlants';
import { useAuth } from '../hooks/useAuth';
import { useGrowingZone } from '../hooks/useGrowingZone';
import { supabase, retryOperation } from '../lib/supabase';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import { generatePlantsListBreadcrumb } from '../utils/breadcrumb';
import { Loader2, Grid, List, AlertCircle, RefreshCw, Search } from 'lucide-react';
import type { Zone } from '../types/zone';

type ViewMode = 'grid' | 'list';

export default function Plants() {
  const { zone } = useGrowingZone();
  const [selectedZone, setSelectedZone] = useState<Zone | null>(() => zone);
  const [selectedDifficulty, setSelectedDifficulty] = useState<string | null>(null);
  const [selectedSunlight, setSelectedSunlight] = useState<string | null>(null);
  const [selectedSeason, setSelectedSeason] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const { plants, loading, error } = usePlants(
    selectedZone, 
    selectedDifficulty as any,
    selectedSunlight,
    selectedSeason
  );
  const { setItems } = useBreadcrumb();
  const { user } = useAuth();
  const [gardenPlantIds, setGardenPlantIds] = useState<Set<string>>(new Set());
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const [gardenError, setGardenError] = useState<string | null>(null);
  const [retryingGarden, setRetryingGarden] = useState(false);
  const letterRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const retryTimeoutRef = useRef<number>();

  // Update selected zone when user's zone changes
  React.useEffect(() => {
    setSelectedZone(zone);
  }, [zone]);

  React.useEffect(() => {
    setItems(generatePlantsListBreadcrumb());
  }, [setItems]);

  const fetchGardenPlants = React.useCallback(async (retryCount = 0) => {
    if (!user) return;

    try {
      setGardenError(null);
      const { data, error } = await retryOperation(() =>
        supabase
          .from('user_gardens')
          .select('plant_id')
          .eq('user_id', user.id),
        3, // max retries
        1000, // initial delay
        undefined, // no abort signal
      );

      if (error) throw error;
      setGardenPlantIds(new Set(data?.map(item => item.plant_id) || []));
    } catch (err) {
      console.error('Error fetching garden plants:', err);
      const isNetworkError = err instanceof Error && 
        (err.message.includes('Failed to fetch') || 
         err.message.includes('NetworkError'));

      if (isNetworkError && retryCount < 3) {
        // Clear any existing retry timeout
        if (retryTimeoutRef.current) {
          window.clearTimeout(retryTimeoutRef.current);
        }
        
        // Exponential backoff with jitter
        const delay = Math.min(1000 * Math.pow(2, retryCount), 10000) + 
          Math.random() * 1000;
        
        setGardenError(`Connection issue. Retrying in ${Math.round(delay/1000)}s...`);
        
        retryTimeoutRef.current = window.setTimeout(() => {
          fetchGardenPlants(retryCount + 1);
        }, delay);
      } else {
        setGardenError('Failed to load garden plants. Please try again.');
      }
    } finally {
      setRetryingGarden(false);
    }
  }, [user]);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        window.clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    fetchGardenPlants();
  }, [fetchGardenPlants]);

  const handleRetryGarden = () => {
    setRetryingGarden(true);
    fetchGardenPlants();
  };

  const handleAddSuccess = async () => {
    if (user) {
      try {
        setGardenError(null);
        const { data, error } = await retryOperation(() =>
          supabase
            .from('user_gardens')
            .select('plant_id')
            .eq('user_id', user.id)
        );
        
        if (error) throw error;
        setGardenPlantIds(new Set(data?.map(item => item.plant_id) || []));
      } catch (err) {
        console.error('Error updating garden plants:', err);
        setGardenError('Failed to update garden status. Please refresh the page.');
      }
    }
  };

  // Filter plants based on search query
  const filteredPlants = plants.filter(plant => {
    const searchLower = searchQuery.toLowerCase();
    return (
      plant.name.toLowerCase().includes(searchLower) ||
      plant.scientific_name.toLowerCase().includes(searchLower) ||
      plant.description?.toLowerCase().includes(searchLower) ||
      false
    );
  });

  // Group plants by first letter
  const plantsByLetter = filteredPlants.reduce((acc: { [key: string]: typeof plants }, plant) => {
    const firstLetter = plant.name.charAt(0).toUpperCase();
    if (!acc[firstLetter]) {
      acc[firstLetter] = [];
    }
    acc[firstLetter].push(plant);
    return acc;
  }, {});

  // Get unique letters that have plants
  const availableLetters = Object.keys(plantsByLetter).sort();

  // Handle letter click
  const handleLetterClick = (letter: string) => {
    setSelectedLetter(letter);
    const element = letterRefs.current[letter];
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };


  if (error) {
    return (
      <div className="container mx-auto px-4">
        <Breadcrumb />
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="text-red-700">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4">
      <Breadcrumb />
      
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-3xl font-bold">Browse Plants</h1>
        <div className="flex items-center gap-4 w-full sm:w-auto">
          {/* Search Input */}
          <div className="relative flex-1 sm:w-64">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search plants..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-primary-500 focus:border-primary-500"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          </div>
          <div className="flex items-center gap-2 bg-white rounded-lg border p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded ${
                viewMode === 'grid' 
                  ? 'bg-primary-500 text-white' 
                  : 'text-gray-600 hover:text-primary-600'
              }`}
              title="Grid view"
            >
              <Grid className="h-5 w-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded ${
                viewMode === 'list' 
                  ? 'bg-primary-500 text-white' 
                  : 'text-gray-600 hover:text-primary-600'
              }`}
              title="List view"
            >
              <List className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      <FiltersBar
        selectedZone={selectedZone}
        onZoneChange={setSelectedZone}
        selectedDifficulty={selectedDifficulty}
        onDifficultyChange={setSelectedDifficulty}
        selectedSunlight={selectedSunlight}
        onSunlightChange={setSelectedSunlight}
        selectedSeason={selectedSeason}
        onSeasonChange={setSelectedSeason}
      />

      {gardenError && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-yellow-500 flex-shrink-0" />
            <p className="text-yellow-700 text-sm">{gardenError}</p>
          </div>
          <button
            onClick={handleRetryGarden}
            disabled={retryingGarden}
            className="flex items-center gap-2 text-sm text-yellow-600 hover:text-yellow-700 disabled:opacity-50"
          >
            {retryingGarden ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            <span>Retry</span>
          </button>
        </div>
      )}

      {/* Alphabetical Glossary */}
      <div className="sticky top-0 z-10 bg-white border-b mb-6 py-3">
        <div className="flex flex-wrap gap-2 justify-center">
          {Array.from('ABCDEFGHIJKLMNOPQRSTUVWXYZ').map(letter => {
            const isAvailable = availableLetters.includes(letter);
            return (
              <button
                key={letter}
                onClick={() => isAvailable && handleLetterClick(letter)}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  isAvailable
                    ? selectedLetter === letter
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-primary-100'
                    : 'bg-gray-50 text-gray-300 cursor-not-allowed'
                }`}
                disabled={!isAvailable}
              >
                {letter}
              </button>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      ) : (
        <div>
          {availableLetters.map(letter => (
            <div 
              key={letter}
              ref={el => letterRefs.current[letter] = el}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">
                {letter}
              </h2>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {plantsByLetter[letter].map((plant) => (
                    <div key={plant.id} className="h-full">
                      <PlantCard
                        plant={plant}
                        isInGarden={gardenPlantIds.has(plant.id)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-md divide-y">
                  {plantsByLetter[letter].map((plant) => (
                    <PlantListItem
                      key={plant.id}
                      plant={plant}
                      isInGarden={gardenPlantIds.has(plant.id)}
                      onAddSuccess={handleAddSuccess}
                    />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}